import { useState, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ActionBarShell } from './ActionBarShell';
import { ActionBarItem } from './ActionBarItem';
import { ActionBarToggle } from './ActionBarToggle';
import { ActionBarMenu } from './ActionBarMenu';
import { ActionBarMenuItem } from './ActionBarMenuItem/ActionBarMenuItem';
import { ActionBarDivider } from './ActionBarDivider';
import { ActionBarSearch } from './ActionBarSearch';
import { DropdownMenuSeparator } from '@/components/ui/DropdownMenu/DropdownMenu';
import { AutoHideOnScroll } from '../AutoHideOnScroll';
import { usePermissions } from '@/hooks/usePermissions';
import { usePopoverCloseSignal } from '@/stores';
import { Permission } from '@/types/permissions';
import { ActionType } from './ActionBar.types';
import type {
    ActionConfig,
    ItemAction,
    ToggleAction,
    MenuAction,
    MenuItemSeparator,
    SearchAction,
    CustomAction,
} from './ActionBar.types';

function isMenuItemSeparator(
    item: MenuAction['items'][number],
): item is MenuItemSeparator {
    return 'separator' in item && item.separator === true;
}

interface ActionBarProps {
    actions: ActionConfig[];
    scrollContainer?: HTMLDivElement | null;
}

function hasPermissionField(
    action: ActionConfig,
): action is ActionConfig & { permission: Permission } {
    return 'permission' in action && action.permission !== undefined;
}

function ActionBarItemWithPopover({ action }: { action: ItemAction }) {
    const [isOpen, setIsOpen] = useState(false);
    const closeSignal = usePopoverCloseSignal(
        (state: { signal: number; trigger: () => void }) => state.signal,
    );

    useEffect(() => {
        if (closeSignal > 0) {
            setIsOpen(false);
        }
    }, [closeSignal]);

    const disabledHoverHandlers = action.disabled
        ? {
              onPointerEnter: () => setIsOpen(true),
              onPointerLeave: () => setIsOpen(false),
          }
        : {};

    return (
        <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
            <Popover.Trigger asChild>
                <div {...disabledHoverHandlers}>
                    <ActionBarItem
                        icon={action.icon}
                        label={action.label}
                        onClear={action.onClear}
                        disabled={action.disabled}
                        active={action.active}
                        maxWidth={action.maxWidth}
                        variant={action.variant}
                        className={
                            action.disabled
                                ? 'cursor-default opacity-50'
                                : undefined
                        }
                        trailingBadge={action.trailingBadge}
                    />
                </div>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    side="bottom"
                    align="start"
                    sideOffset={8}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onPointerEnter={() => action.disabled && setIsOpen(true)}
                    onPointerLeave={() => action.disabled && setIsOpen(false)}
                    className="z-50"
                >
                    {action.content}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

function renderItem(action: ItemAction) {
    if (action.content) {
        return <ActionBarItemWithPopover action={action} />;
    }

    return (
        <ActionBarItem
            icon={action.icon}
            label={action.label}
            onClick={action.onClick}
            onClear={action.onClear}
            disabled={action.disabled}
            active={action.active}
            maxWidth={action.maxWidth}
            variant={action.variant}
        />
    );
}

function renderToggle(action: ToggleAction) {
    return (
        <ActionBarToggle
            icon={action.icon}
            activeIcon={action.activeIcon}
            indeterminateIcon={action.indeterminateIcon}
            label={action.label}
            isActive={action.isActive}
            indeterminate={action.indeterminate}
            onClick={action.onClick}
            slideContent={action.slideContent}
        />
    );
}

function renderMenu(action: MenuAction) {
    return (
        <ActionBarMenu
            icon={action.icon}
            label={action.label}
            trigger={action.trigger}
            title={action.title}
            active={action.active}
            onClear={action.onClear}
            variant={action.variant}
            align={action.dropdownAlign}
            disabled={action.disabled}
            triggerTestId={action.triggerTestId}
        >
            {action.items.map((item, itemIndex) =>
                isMenuItemSeparator(item) ? (
                    <DropdownMenuSeparator key={itemIndex} />
                ) : (
                    <ActionBarMenuItem
                        key={itemIndex}
                        icon={item.icon}
                        label={item.label}
                        onClick={item.onClick}
                        disabled={item.disabled}
                        isActive={item.isActive}
                        activeIndicator={
                            item.activeIndicator ?? action.activeIndicator
                        }
                        alignOffset={item.alignOffset}
                        trailingBadge={item.trailingBadge}
                        testId={item.testId}
                    >
                        {item.children}
                    </ActionBarMenuItem>
                ),
            )}
        </ActionBarMenu>
    );
}

function renderCustom(action: CustomAction) {
    return <>{action.content}</>;
}

export function ActionBar({ actions, scrollContainer }: ActionBarProps) {
    const { has } = usePermissions();
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);

    const visibleActions = actions.filter(
        (action) => !hasPermissionField(action) || has(action.permission),
    );

    const renderSearch = (action: SearchAction) => (
        <ActionBarSearch
            value={action.value}
            onChange={action.onChange}
            onSubmit={action.onSubmit}
            placeholder={action.placeholder}
            isExpanded={action.isExpanded ?? isSearchExpanded}
            onExpandedChange={action.onExpandedChange ?? setIsSearchExpanded}
        />
    );

    const renderAction = (action: ActionConfig, index: number) => {
        const element = (() => {
            switch (action.type) {
                case ActionType.ITEM:
                    return renderItem(action);
                case ActionType.TOGGLE:
                    return renderToggle(action);
                case ActionType.MENU:
                    return renderMenu(action);
                case ActionType.SEARCH:
                    return renderSearch(action);
                case ActionType.DIVIDER:
                    return (
                        <ActionBarDivider orientation={action.orientation} />
                    );
                case ActionType.CUSTOM:
                    return renderCustom(action);
            }
        })();

        return <div key={index}>{element}</div>;
    };

    const bar = (
        <ActionBarShell>
            <div className="flex items-center gap-1">
                {visibleActions.map(renderAction)}
            </div>
        </ActionBarShell>
    );

    if (scrollContainer !== undefined) {
        return (
            <AutoHideOnScroll container={scrollContainer}>
                {bar}
            </AutoHideOnScroll>
        );
    }

    return bar;
}
